/**
 * PO Lines Table Component
 * Displays the main data table with headers and rows
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Button } from '../../../ui/button';
import { Pagination } from '../../../ui/pagination';
import {
  Package,
  Search,
  Loader2,
  Check,
  X,
  Pause,
  Eye,
  EyeOff,
  Download,
  GripVertical,
  Columns,
} from 'lucide-react';
import { PurchaseOrderLine, ColumnConfig, NextActionResult, POLinesFilters } from './types';
import { getSortIcon } from './utils';
import type { User } from '../../../../lib/api';
import { POLineRow } from './POLineRow';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useAuth } from '../../../../contexts/AuthContext';

// CR-3846: Column header configuration for dynamic rendering
interface ColumnHeaderConfig {
  align: 'left' | 'center' | 'right';
  sortable: boolean;
  sortField?: string;
}

// CR-3850: Added additional column headers
const COLUMN_HEADER_CONFIG: Record<string, ColumnHeaderConfig> = {
  acknowledge_status: { align: 'left', sortable: true, sortField: 'acknowledge_status' },
  action: { align: 'center', sortable: false },
  is_critical: { align: 'center', sortable: true, sortField: 'is_critical' },
  on_hold: { align: 'center', sortable: true, sortField: 'on_hold' },
  hold_reason: { align: 'left', sortable: true, sortField: 'hold_reason' },
  next_action: { align: 'center', sortable: true, sortField: 'next_action' },
  po_number: { align: 'left', sortable: true, sortField: 'po_number' },
  line_number: { align: 'center', sortable: true, sortField: 'line_number' },
  customer_name: { align: 'left', sortable: true, sortField: 'customer_name' },
  supplier_name: { align: 'left', sortable: true, sortField: 'supplier_name' },
  supplier_group_name: { align: 'left', sortable: true, sortField: 'supplier_group_name' },
  part_number: { align: 'left', sortable: true, sortField: 'part_number' },
  description: { align: 'left', sortable: true, sortField: 'description' },
  quantity: { align: 'right', sortable: true, sortField: 'quantity' },
  received_qty: { align: 'right', sortable: true, sortField: 'received_qty' },
  outstanding_qty: { align: 'right', sortable: true, sortField: 'outstanding_qty' },
  pct_delivered: { align: 'center', sortable: true, sortField: 'pct_delivered' },
  unit_price: { align: 'right', sortable: true, sortField: 'unit_price' },
  total_price: { align: 'right', sortable: true, sortField: 'total_price' },
  currency: { align: 'center', sortable: true, sortField: 'currency' },
  delivery_date: { align: 'left', sortable: true, sortField: 'delivery_date' },
  supplier_promise_date: { align: 'left', sortable: true, sortField: 'supplier_promise_date' },
  buyer_id: { align: 'left', sortable: true, sortField: 'buyer_id' },
  batch_serial_no: { align: 'left', sortable: true, sortField: 'batch_serial_no' },
  ppv: { align: 'right', sortable: true, sortField: 'ppv' },
  otif_overridden: { align: 'center', sortable: true, sortField: 'otif_overridden' },
  packing_note_number: { align: 'left', sortable: true, sortField: 'packing_note_number' },
  order_date: { align: 'left', sortable: true, sortField: 'order_date' },
  last_received_date: { align: 'left', sortable: true, sortField: 'last_received_date' },
  updated_at: { align: 'left', sortable: true, sortField: 'updated_at' },
  required_date_change_count: { align: 'center', sortable: true, sortField: 'required_date_change_count' },
  promised_date_change_count: { align: 'center', sortable: true, sortField: 'promised_date_change_count' },
  pdf_attached_count: { align: 'center', sortable: true, sortField: 'pdf_attached_count' },
  material_cert_count: { align: 'center', sortable: true, sortField: 'material_cert_count' },
  contract_unit_price: { align: 'right', sortable: true, sortField: 'contract_unit_price' },
  contract_lot_price: { align: 'right', sortable: true, sortField: 'contract_lot_price' },
  drawing_number: { align: 'left', sortable: true, sortField: 'drawing_number' },
  drawing_version: { align: 'left', sortable: true, sortField: 'drawing_version' },
  outstanding_value: { align: 'right', sortable: true, sortField: 'outstanding_value' },
  converted_unit_price: { align: 'right', sortable: true, sortField: 'converted_unit_price' },
  converted_total_value: { align: 'right', sortable: true, sortField: 'converted_total_value' },
  converted_outstanding_value: { align: 'right', sortable: true, sortField: 'converted_outstanding_value' },
  original_promise_date: { align: 'left', sortable: true, sortField: 'original_promise_date' },
  mrp_scheduled_date: { align: 'left', sortable: true, sortField: 'mrp_scheduled_date' },
  priority: { align: 'center', sortable: true, sortField: 'priority' },
  contract_price: { align: 'right', sortable: true, sortField: 'contract_price' },
  exchange_rate: { align: 'right', sortable: true, sortField: 'exchange_rate' },  // CR-4679
};

interface POLinesTableProps {
  lines: PurchaseOrderLine[];
  totalCount: number;
  needsActionCount: number | null;
  openCount: number | null;
  closedCount: number | null;
  onHoldCount: number | null;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  activeTab: string;
  showFilters: boolean;
  exporting: boolean;
  user: User | null;
  columns: ColumnConfig[];
  showColumnDropdown: boolean;
  columnDropdownRef: React.RefObject<HTMLDivElement | null>;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  selectedLineIds: Set<number>;
  bulkAccepting: boolean;
  bulkRejecting: boolean;
  bulkHolding: boolean;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  onSetActiveTab: (tab: string) => void;
  onSetShowFilters: (show: boolean) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort: (field: string) => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  onToggleColumn: (columnId: string) => void;
  onResetColumns: () => void;
  onSelectAllColumns: () => void;
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void;
  onSetShowColumnDropdown: (show: boolean) => void;
  onClearFilters: () => void;
  onSelectLine: (lineId: number) => void;
  onReviewChanges: (lineId: number) => void;  // CR-4728
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onToggleCritical: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  onSelectAll: () => void;
  onToggleSelect: (lineId: number) => void;
  onBulkAccept: () => void;
  onBulkReject: () => void;
  onBulkHold: () => void;
  onSetSelectedLineIds: (ids: Set<number>) => void;
  onImportPromiseDates: () => void;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  getNextAction: (line: PurchaseOrderLine) => NextActionResult;
  getSelectableLines: () => PurchaseOrderLine[];
  canSelectForBulkAccept: (line: PurchaseOrderLine) => boolean;
  filters: POLinesFilters;
}

// Columns hidden in the PO Lines table when schedule_collaboration is enabled
// (these are managed at the schedule level instead).
const SCHEDULE_COLLAB_HIDDEN_TABLE_COLUMNS = new Set([
  'acknowledge_status',
  'action',
  'next_action',
  'on_hold',
  'hold_reason',
  'supplier_promise_date',
]);

export function POLinesTable({
  lines, totalCount, needsActionCount, openCount, closedCount, onHoldCount, currentPage, totalPages, pageSize,
  activeTab, showFilters, exporting, user, columns, showColumnDropdown,
  columnDropdownRef, sortField, sortDirection, selectedLineIds,
  bulkAccepting, bulkRejecting, bulkHolding,
  formatDate, formatCurrency,
  onSetActiveTab, onSetShowFilters, onPageChange, onPageSizeChange, onSort, onExport,
  onToggleColumn, onResetColumns, onSelectAllColumns, onReorderColumns,
  onSetShowColumnDropdown, onClearFilters, onSelectLine, onReviewChanges, onSelectLineForWorkflow,
  onProposeChange, onToggleCritical, onPutOnHold, onReleaseHold,
  onSelectAll, onToggleSelect, onBulkAccept, onBulkReject, onBulkHold,
  onSetSelectedLineIds, onImportPromiseDates,
  shouldShowActions, shouldShowDecline, shouldShowProposeChange,
  getNextAction, getSelectableLines, canSelectForBulkAccept, filters,
}: POLinesTableProps) {
  // When schedule_collaboration is enabled, the PO Lines tab becomes a read-only
  // order-data view. Strip collab columns from the visible set even if the user
  // had toggled them on previously.
  const { isFeatureEnabled, isOpenOrdersControlHidden } = useMenuSettings();
  const { user: authUser } = useAuth();
  const isSupplier = authUser?.platform === 'SUPPLIER';
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  // CR-5884: Open Orders supplier-side hide-toggles (Admin Setup → Purchasing).
  const hideExceptions = isOpenOrdersControlHidden('hide_exceptions', isSupplier);
  const hideActionsColumn = isOpenOrdersControlHidden('hide_actions', isSupplier);
  let visibleColumns = scheduleCollabEnabled
    ? columns.filter(col => !SCHEDULE_COLLAB_HIDDEN_TABLE_COLUMNS.has(col.id))
    : columns;
  if (hideActionsColumn) {
    visibleColumns = visibleColumns.filter(col => col.id !== 'action');
  }
  // CR-4687 round 3: ≥2 rows selected = bulk mode. Per-line action buttons in
  // each row render disabled while this is true; the only allowed actions are
  // the bulk buttons in the header bar (or clearing the selection).
  const bulkMode = selectedLineIds.size >= 2;
  return (
    <Card data-tour="po-lines-table">
      <Tabs value={activeTab} onValueChange={onSetActiveTab}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>PO Line Items</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Showing {lines.length} of {totalCount.toLocaleString()} line items
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => onSetShowFilters(!showFilters)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                <Search className="h-3 w-3" />Filters
              </button>
              <button onClick={() => onExport('xlsx')} disabled={exporting || lines.length === 0} className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed" title="Export filtered data to Excel">
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}Export
              </button>
              <button onClick={onImportPromiseDates} className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted" title="Import latest supplier promise dates from CSV/Excel">
                <Package className="h-3 w-3" />Import Promise Dates
              </button>

              {/* Column Visibility Dropdown */}
              <div className="relative" ref={columnDropdownRef}>
                <button onClick={() => onSetShowColumnDropdown(!showColumnDropdown)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showColumnDropdown ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  <Columns className="h-3 w-3" />Columns
                </button>
                {showColumnDropdown && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                    <div className="p-2 border-b flex items-center justify-between">
                      <span className="text-sm font-medium">Manage Columns</span>
                      <div className="flex items-center gap-2">
                        <button onClick={onSelectAllColumns} className="text-xs text-primary hover:underline">Select All</button>
                        <span className="text-xs text-muted-foreground">|</span>
                        <button onClick={onResetColumns} className="text-xs text-primary hover:underline">Reset</button>
                      </div>
                    </div>
                    <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">Drag to reorder - Click to show/hide</div>
                    <div className="p-2 space-y-1">
                      {columns.map((col, index) => (
                        <div
                          key={col.id}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('text/plain', index.toString()); e.currentTarget.classList.add('opacity-50'); }}
                          onDragEnd={(e) => { e.currentTarget.classList.remove('opacity-50'); }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/10'); }}
                          onDragLeave={(e) => { e.currentTarget.classList.remove('bg-primary/10'); }}
                          onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/10'); const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10); if (dragIndex !== index) onReorderColumns(dragIndex, index); }}
                          className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group"
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                          <input type="checkbox" checked={col.visible} onChange={() => onToggleColumn(col.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                          <span className="text-sm flex-1">{col.label}</span>
                          {col.visible ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <TabsList data-tour="quick-filters" className="mt-3 justify-start">
            <TabsTrigger value="all">All Lines</TabsTrigger>
            {/* CR-5884: Exceptions tab respects supplier-side hide_exceptions toggle. */}
            {!scheduleCollabEnabled && !hideExceptions && (
              <TabsTrigger value="needs_action" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                Exceptions{needsActionCount !== null && needsActionCount > 0 ? ` (${needsActionCount})` : ''}
              </TabsTrigger>
            )}
            <TabsTrigger value="open">Open{openCount !== null && openCount > 0 ? ` (${openCount.toLocaleString()})` : ''}</TabsTrigger>
            <TabsTrigger value="closed">Closed{closedCount !== null && closedCount > 0 ? ` (${closedCount.toLocaleString()})` : ''}</TabsTrigger>
            {!scheduleCollabEnabled && (
              <TabsTrigger value="on_hold" data-tour="on-hold-filter">On Hold{onHoldCount !== null && onHoldCount > 0 ? ` (${onHoldCount.toLocaleString()})` : ''}</TabsTrigger>
            )}
          </TabsList>
        </CardHeader>

        <TabsContent value={activeTab} className="m-0">
          <CardContent>
            {lines.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{Object.values(filters).some(v => v) ? 'No lines match your filters' : 'No line items found'}</p>
                {Object.values(filters).some(v => v) && (<button onClick={onClearFilters} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>)}
              </div>
            ) : (
              <>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} itemLabel="lines" />

                {/* Bulk Action Bar - hidden when schedule_collaboration is enabled */}
                {!scheduleCollabEnabled && (
                <div className={`mb-4 p-3 border rounded-lg flex items-center gap-3 ${bulkMode ? 'bg-amber-50 border-amber-300' : 'bg-muted/40 border-border'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${bulkMode ? 'text-amber-900 font-medium' : 'text-muted-foreground'}`}>
                      {selectedLineIds.size > 0 ? `${selectedLineIds.size} line${selectedLineIds.size > 1 ? 's' : ''} selected` : 'No lines selected'}
                    </span>
                    {/* CR-4687 round 3: explicit bulk-mode indicator so testers know per-line buttons are intentionally disabled. */}
                    {bulkMode && (
                      <span className="text-xs text-amber-800">
                        — bulk mode: per-line actions disabled. Use the buttons here, or clear the selection.
                      </span>
                    )}
                    {selectedLineIds.size > 0 && (<button onClick={() => onSetSelectedLineIds(new Set())} className="text-xs text-muted-foreground underline hover:text-foreground">Clear</button>)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={onBulkHold} disabled={selectedLineIds.size === 0 || bulkHolding} className={selectedLineIds.size > 0 ? 'border-orange-500 text-orange-700 hover:bg-orange-50' : 'opacity-60 cursor-not-allowed'}>
                      {bulkHolding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Holding...</> : <><Pause className="h-4 w-4 mr-2" />Put On Hold All</>}
                    </Button>
                    <Button size="sm" onClick={onBulkAccept} disabled={selectedLineIds.size === 0 || bulkAccepting} className={selectedLineIds.size > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-200 text-green-600 cursor-not-allowed opacity-60'}>
                      {bulkAccepting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Accepting...</> : <><Check className="h-4 w-4 mr-2" />Accept All</>}
                    </Button>
                    <Button size="sm" onClick={onBulkReject} disabled={selectedLineIds.size === 0 || bulkRejecting} className={selectedLineIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-200 text-red-400 cursor-not-allowed opacity-60'}>
                      {bulkRejecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</> : <><X className="h-4 w-4 mr-2" />Reject All</>}
                    </Button>
                  </div>
                </div>
                )}

                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20">
                      <tr className="border-b bg-muted/80 backdrop-blur-sm">
                        {!scheduleCollabEnabled && (
                          <th className="text-center py-3 px-2 font-medium" style={{ minWidth: '50px' }}>
                            <input type="checkbox" checked={getSelectableLines().length > 0 && selectedLineIds.size === getSelectableLines().length} onChange={onSelectAll} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" title="Select all eligible lines" />
                          </th>
                        )}
                        {visibleColumns.filter(col => col.visible).map(col => {
                          const config = COLUMN_HEADER_CONFIG[col.id];
                          if (!config) return null;
                          const alignClass = config.align === 'center' ? 'text-center' : config.align === 'right' ? 'text-right' : 'text-left';
                          const justifyClass = config.align === 'center' ? 'justify-center' : config.align === 'right' ? 'justify-end' : '';
                          return (
                            <th key={col.id} className={`${alignClass} py-3 px-4 font-medium ${config.sortable ? 'cursor-pointer hover:bg-muted/50' : ''}`} style={{ minWidth: col.minWidth }} onClick={config.sortable && config.sortField ? () => onSort(config.sortField!) : undefined}>
                              {config.sortable ? (<div className={`flex items-center gap-1 ${justifyClass}`}>{col.label} {getSortIcon(config.sortField!, sortField, sortDirection)}</div>) : col.label}
                            </th>
                          );
                        })}
                        <th className="text-center py-3 px-4 font-medium" style={{ minWidth: '80px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <POLineRow
                          key={line.id} line={line} user={user} columns={visibleColumns}
                          scheduleCollabEnabled={scheduleCollabEnabled} bulkMode={bulkMode}
                          selectedLineIds={selectedLineIds} formatDate={formatDate} formatCurrency={formatCurrency}
                          shouldShowActions={shouldShowActions} shouldShowDecline={shouldShowDecline}
                          shouldShowProposeChange={shouldShowProposeChange} getNextAction={getNextAction}
                          canSelectForBulkAccept={canSelectForBulkAccept} onSelectLine={onSelectLine}
                          onReviewChanges={onReviewChanges}
                          onSelectLineForWorkflow={onSelectLineForWorkflow} onProposeChange={onProposeChange}
                          onToggleCritical={onToggleCritical} onPutOnHold={onPutOnHold}
                          onReleaseHold={onReleaseHold} onToggleSelect={onToggleSelect}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} itemLabel="lines" />
              </>
            )}
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
